import type {
  AdminRolesResponse,
  AdminUserResponse,
  AdminUsersListResponse,
  CreateAdminUserRequest,
  LoginSuccessResponse,
  MeSuccessResponse,
  SaveDataPayload,
  SaveDataError,
  CatalogProductAutofillPayload,
  ProgramDealStatsPayload,
  ProgramDealStatsRequest,
  SystemUpdateStatusResponse,
  TeachersCatalogSuccessResponse,
  OrganizationsSearchResponse,
  UpdateAdminUserPasswordRequest,
  UpdateAdminUserRequest,
  UpdateAdminUserRolesRequest
} from './types';

type ApiErrorBody = {
  success?: false;
  message?: string;
  errors?: string[];
  fieldErrors?: Record<string, string>;
};

const API_PREFIX = '/api';

function apiPath(path: string) {
  return `${API_PREFIX}${path}`;
}

export type SuccessResponse = { success: true; message?: string };

export class ApiError extends Error {
  readonly isAuthError: boolean;

  constructor(
    public readonly status: number,
    message: string,
    public readonly errors: string[] = [],
    public readonly fieldErrors: Record<string, string> = {}
  ) {
    super(message);
    this.name = 'ApiError';
    this.isAuthError = status === 401 || status === 403;
  }
}

export async function parseJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(response.status, 'Сервер вернул некорректный JSON');
  }
}

export function toApiError(response: Response, body?: ApiErrorBody) {
  const errors = Array.isArray(body?.errors) ? body.errors : [];
  const message =
    errors.length > 0 ? errors.join('\n') : body?.message || `Ошибка запроса (${response.status})`;
  return new ApiError(response.status, message, errors, body?.fieldErrors ?? {});
}

async function parseBlobErrorBody(response: Response): Promise<ApiErrorBody | undefined> {
  const text = await response.text();
  if (!text) return undefined;

  try {
    return JSON.parse(text) as ApiErrorBody;
  } catch {
    return { message: text };
  }
}

export function toNetworkError(error: unknown) {
  if (error instanceof ApiError) return error;
  if (error instanceof TypeError) {
    return new ApiError(0, 'Не удалось подключиться к серверу. Проверьте соединение и повторите попытку.');
  }

  return error;
}

export async function requestJson<T>(path: string, options: RequestInit = {}) {
  let response: Response;
  try {
    response = await fetch(apiPath(path), { credentials: 'include', ...options });
  } catch (error) {
    throw toNetworkError(error);
  }

  const body = await parseJson<T | ApiErrorBody>(response);

  if (
    !response.ok ||
    (typeof body === 'object' && body !== null && 'success' in body && body.success === false)
  ) {
    throw toApiError(response, body as ApiErrorBody);
  }

  return body as T;
}

export async function requestBlob(path: string, options: RequestInit = {}) {
  let response: Response;
  try {
    response = await fetch(apiPath(path), { credentials: 'include', ...options });
  } catch (error) {
    throw toNetworkError(error);
  }

  if (!response.ok) {
    throw toApiError(response, await parseBlobErrorBody(response));
  }

  return response.blob();
}

export function jsonRequestInit(method: string, payload?: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: payload === undefined ? undefined : JSON.stringify(payload)
  };
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

export const apiClient = {
  getTeachers: () => requestJson<TeachersCatalogSuccessResponse>('/teachers'),
  getCatalogProductAutofill: (productId: string, signal?: AbortSignal) =>
    requestJson<CatalogProductAutofillPayload>(
      `/catalog/products/${encodeURIComponent(productId)}/autofill`,
      { signal }
    ),
  getProgramDealStats: (payload: ProgramDealStatsRequest, signal?: AbortSignal) =>
    requestJson<ProgramDealStatsPayload>('/catalog/program-deal-stats', {
      ...jsonRequestInit('POST', payload),
      signal
    }),
  searchOrganizations: (query: string) =>
    requestJson<OrganizationsSearchResponse>(`/organizations/search?query=${encodeURIComponent(query)}`),
  login: (payload: { username: string; password: string }) =>
    requestJson<LoginSuccessResponse>('/login', jsonRequestInit('POST', payload)),
  logout: () => requestJson<SuccessResponse>('/logout', { method: 'POST' }),
  me: () => requestJson<MeSuccessResponse>('/me'),
  getAdminUsers: (queryString = '') => requestJson<AdminUsersListResponse>(`/admin/users${queryString}`),
  getAdminRoles: () => requestJson<AdminRolesResponse>('/admin/roles'),
  createAdminUser: (payload: CreateAdminUserRequest) =>
    requestJson<AdminUserResponse>('/admin/users', jsonRequestInit('POST', payload)),
  updateAdminUser: (id: number, payload: UpdateAdminUserRequest) =>
    requestJson<AdminUserResponse>(`/admin/users/${id}`, jsonRequestInit('PATCH', payload)),
  updateAdminUserPassword: (id: number, payload: UpdateAdminUserPasswordRequest) =>
    requestJson<SuccessResponse>(`/admin/users/${id}/password`, jsonRequestInit('PATCH', payload)),
  updateAdminUserRoles: (id: number, payload: UpdateAdminUserRolesRequest) =>
    requestJson<AdminUserResponse>(`/admin/users/${id}/roles`, jsonRequestInit('PUT', payload)),
  getSystemUpdateStatus: () => requestJson<SystemUpdateStatusResponse>('/system/update-status'),
  runSystemMigrations: () =>
    requestJson<SystemUpdateStatusResponse>('/system/run-migrations', jsonRequestInit('POST')),
  saveData: (data: SaveDataPayload) =>
    requestJson<SuccessResponse | SaveDataError>('/save-data', jsonRequestInit('POST', data)),
  deleteLastRecord: () => requestJson<SuccessResponse>('/delete-last-record', jsonRequestInit('POST'))
};
